/**
 * HTML5-Video-Tracking für Blog-Videos (GA4 video_start / video_progress / video_complete).
 *
 * Baut das GA4-YouTube-"Video engagement"-Verhalten für unsere Shopify-gehosteten MP4s nach
 * und published die Events als 'nt_video' ins Shopify Analytics Layer. Dort greift der
 * Custom Pixel sie via analytics.subscribe('nt_video') ab und pusht sie in den GTM-dataLayer.
 *
 * Geladen nur auf Artikelseiten (layout/theme.liquid). Erfasst dort jedes <video>, auch
 * solche, die erst später ins DOM kommen (deferred-media klont aus <template>).
 *
 * Nicht über src/ gebaut: rollup nimmt jede Datei in src/ als Entry-Point, ein Build würde
 * alle Assets neu schreiben. Bei Änderungen also diese Datei direkt editieren.
 * Test: src/scripts/nt-video-tracking.test.js
 */

(() => {
  if (window.__ntVideoTracking) return;
  window.__ntVideoTracking = true;

  const EVENT_NAME = 'nt_video'; // muss zur Custom-Pixel-Subscription passen
  const MILESTONES = [25, 50, 75];
  const TITLE_MAX = 100;
  const DEBUG = location.search.indexOf('nt_video_debug') !== -1;

  // --- Consent: gleiche Kategorie wie die GA4-Tags (analytics), fail closed ---------------
  let analyticsAllowed = false;

  function readConsent() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      analyticsAllowed = !!(privacy && privacy.analyticsProcessingAllowed());
    } catch (error) {
      analyticsAllowed = false;
    }
  }

  function canPublish() {
    const shopify = window.Shopify;
    return !!(
      analyticsAllowed &&
      shopify &&
      shopify.analytics &&
      typeof shopify.analytics.publish === 'function'
    );
  }

  // --- Payload-Bausteine -----------------------------------------------------------------
  function sourceUrl(el) {
    if (el.currentSrc) return el.currentSrc;
    const source = el.querySelector('source[src]');
    return (source && source.src) || el.src || '';
  }

  // Shopify-CDN-Pfad: /videos/c/vp/<hash>/<hash>.HD-1080p-….mp4
  function videoId(url) {
    const match = /\/videos\/c\/vp\/([^/]+)\//.exec(url);
    if (match) return match[1];
    const file = url.split('?')[0].split('/').pop() || '';
    return file.replace(/\.[a-z0-9]+$/i, '');
  }

  function videoTitle(el) {
    const carrier = el.closest('[data-nt-video-title]');
    let title =
      (carrier && carrier.getAttribute('data-nt-video-title')) ||
      el.getAttribute('title') ||
      el.getAttribute('aria-label') ||
      '';

    if (!title) {
      // erste NICHT-leere Überschrift der Section — video-with-text rendert davor ein
      // leeres <h3> (Subheading-Block ohne Inhalt), querySelector('h1,h2,h3') würde das treffen
      const scope = el.closest('.shopify-section') || document;
      title =
        Array.from(scope.querySelectorAll('h1, h2, h3'))
          .map((node) => node.textContent.trim())
          .find(Boolean) || '';
    }

    if (!title) {
      const poster = el.querySelector('img[alt]');
      title = (poster && poster.getAttribute('alt')) || '';
    }

    if (!title) title = videoId(sourceUrl(el));

    return title.replace(/\s+/g, ' ').trim().slice(0, TITLE_MAX);
  }

  function isVisible(el) {
    // keine ClientRects = display:none → fängt das per CSS versteckte Desktop-/Mobile-Duplikat
    if (!el.getClientRects().length) return false;
    const rect = el.getBoundingClientRect();
    return (
      rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth
    );
  }

  function seconds(value) {
    return Number.isFinite(value) ? Math.round(value) : 0;
  }

  /** @returns {boolean} ob das Event wirklich published wurde — Dedupe-Flags hängen daran */
  function send(el, ga4Event, percent) {
    // Gate vor dem Payload-Bau: ein blockiertes Video würde sonst bei jedem timeupdate
    // (~4×/s) videoTitle() und isVisible() aufrufen und damit Layout erzwingen
    if (!canPublish()) {
      if (DEBUG) {
        console.log(
          '[nt_video]',
          ga4Event,
          analyticsAllowed ? 'blocked (publish missing)' : 'blocked (no consent)'
        );
      }
      return false;
    }

    const url = sourceUrl(el);
    const payload = {
      ga4_event: ga4Event,
      video_percent: percent,
      video_title: videoTitle(el),
      video_url: url,
      video_id: videoId(url),
      video_provider: 'shopify',
      video_duration: seconds(el.duration),
      video_current_time: seconds(el.currentTime),
      video_muted: el.muted ? 'true' : 'false',
      visible: isVisible(el) ? 'true' : 'false',
      page_location: location.href,
      page_path: location.pathname,
      page_title: document.title,
    };

    if (DEBUG) console.log('[nt_video]', payload);
    window.Shopify.analytics.publish(EVENT_NAME, payload);
    return true;
  }

  // --- Dedupe je Element und Seitenaufruf -------------------------------------------------
  function sent(el) {
    if (!el.__ntVideoSent) el.__ntVideoSent = {};
    return el.__ntVideoSent;
  }

  /** sendet alle bereits überschrittenen, noch offenen Meilensteine */
  function reportMilestones(el, done) {
    // reine Arithmetik, kein Layout-Zugriff bis ein Meilenstein wirklich greift
    const duration = el.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;
    const percent = (el.currentTime / duration) * 100;
    MILESTONES.forEach((milestone) => {
      if (percent >= milestone && !done[milestone]) {
        done[milestone] = send(el, 'video_progress', milestone);
      }
    });
  }

  function onMediaEvent(event) {
    const el = event.target;
    if (!el || el.tagName !== 'VIDEO') return;
    const done = sent(el);

    if (event.type === 'play') {
      done.played = true; // unabhängig vom Consent — flushPending() braucht das
      if (done.start) return; // Resume nach Pause ist kein neuer Start
      done.start = send(el, 'video_start', 0);
      return;
    }

    // Ohne veröffentlichten video_start keine Folge-Events: verhindert strukturell, dass in
    // GA4 ein Funnel aus Progress/Complete ohne passenden Start entsteht
    if (!done.start) return;

    if (event.type === 'ended') {
      if (done.complete) return; // loop-Videos feuern kein 'ended'
      done.complete = send(el, 'video_complete', 100);
      return;
    }

    reportMilestones(el, done);
  }

  // --- Nachsenden, wenn der Consent erst während der Wiedergabe erteilt wird --------------
  function flushPending() {
    // querySelectorAll steigt nicht in <template>-Inhalte ab, noch nicht geklonte
    // deferred-media-Videos bleiben also unberührt
    Array.prototype.forEach.call(document.querySelectorAll('video'), (el) => {
      const done = el.__ntVideoSent;
      if (!done || !done.played) return; // nie abgespielt → nichts nachzusenden

      if (!done.start) done.start = send(el, 'video_start', 0);
      if (!done.start) return; // Publish weiterhin nicht möglich

      reportMilestones(el, done);
      if (el.ended && !done.complete) done.complete = send(el, 'video_complete', 100);
    });
  }

  function onConsentChange() {
    const wasAllowed = analyticsAllowed;
    readConsent();
    if (!wasAllowed && analyticsAllowed) flushPending();
  }

  // Media-Events bubbeln nicht, laufen aber die Capture-Phase entlang → ein Listener
  // reicht für alle Videos, auch für später eingefügte
  ['play', 'timeupdate', 'ended'].forEach((type) => {
    document.addEventListener(type, onMediaEvent, true);
  });

  // Consent direkt aus der API lesen statt aus event.detail: unabhängig von der Event-Form
  // und damit vom eingesetzten CMP. Auch der loadFeatures-Callback läuft über diesen Pfad,
  // weil er async ist — ein wiederkehrender Besucher mit gespeichertem Consent kann davor
  // schon auf Play gedrückt haben.
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) onConsentChange();
    });
  }
  document.addEventListener('visitorConsentCollected', onConsentChange);
})();
